import React, { useState, useEffect, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import IconButton from "../../../components/IconButton/IconButton";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import GeneralTable from "../../../components/Table/GeneralTable";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";

import axiosInstance from "../../../axiosInstance";
import "./UserRole.scss";
import { toast } from "react-toastify";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import { useFormikContext } from "formik";

const UserRole = ({ markPageAsCompleted }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [bulkUpdateEnabled, setBulkUpdateEnabled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [popupAction, setPopupAction] = useState(() => () => {});
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [initialValues, setInitialValues] = useState({
    roleId: null,
    roleName: "",
    roleSequence: "",
    roleStatus: "Enabled",
    companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
  });
  const [userRoles, setUserRoles] = useState([]);
  const [editableRoles, setEditableRoles] = useState([]);

  // 2. Wrap fetchRoles in useCallback and provide its dependencies
  const fetchRoles = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/roles/all`,
      );
      const roles =
        response.data?.data?.map((role) => ({
          roleId: role.roleId || "N/A",
          roleName: role.roleName || "Unknown",
          roleSequence: role.roleSequence || 0,
          roleStatus: role.roleStatus || "UNKNOWN",
          companyName: role.companyName || "N/A",
        })) || [];

      const sortedRoles = roles.sort((a, b) => a.roleSequence - b.roleSequence);
      setUserRoles(sortedRoles);
      setEditableRoles(sortedRoles.map((role) => ({ ...role })));

      if (roles.length > 0) {
        markPageAsCompleted("User Role");
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  }, [markPageAsCompleted]);

  // 3. Add the now-stable fetchRoles function to useEffect's dependency array
  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleSelectAll = () => {
    const allRoleIds = userRoles.map((role) => role.roleId);
    if (selectAll) {
      setSelectedRoles([]);
      setSelectAll(false);
    } else {
      setSelectedRoles(allRoleIds);
      setSelectAll(true);
    }
  };

  const tableColumns = [
    {
      header: (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <ColoredCheckbox
            name="select-all"
            color="green"
            checked={selectAll}
            onChange={handleSelectAll}
            value="all"
          />
          <span>Role Name</span>
        </div>
      ),
      accessor: "roleName",
    },
    { header: "Sequence", accessor: "roleSequence" },
    { header: "Status", accessor: "status" },
    { header: "Actions", accessor: "actions" },
    { header: "", accessor: "searchableText", hidden: true },
  ];

  const RoleSequenceHelper = ({ userRoles }) => {
    const { values } = useFormikContext();
    const currentSequence = Number(values.roleSequence);
    const matchingRoles = userRoles
      .filter((role) => role.roleSequence === currentSequence)
      .map((role) => role.roleName);
    if (!currentSequence || matchingRoles.length === 0) return null;
    return (
      <div>
        <p className="text-md " style={{ color: "var(--danger-color)" }}>
          Role in use - ({matchingRoles.join(", ")} : {currentSequence})
        </p>
      </div>
    );
  };

  const userRoleValidationSchema = Yup.object().shape({
    roleName: Yup.string().required("Role Name is required"),
    roleSequence: Yup.number()
      .required("Sequence is required")
      .positive("Sequence must be positive")
      .integer("Sequence must be an integer"),
  });

  const handleSelectRole = (roleId) => {
    setSelectedRoles((prev) => {
      if (prev.includes(roleId)) {
        return prev.filter((id) => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  useEffect(() => {
    const allRoleIds = userRoles.map((role) => role.roleId);
    const allSelected =
      allRoleIds.length > 0 &&
      allRoleIds.every((id) => selectedRoles.includes(id));
    setSelectAll(allSelected);
  }, [selectedRoles, userRoles]);

  const handleBatchDelete = () => {
    if (selectedRoles.length === 0) {
      toast.warning("Please select at least one role to delete");
      return;
    }

    setPopupAction(() => async () => {
      try {
        await axiosInstance.delete(`/api/admin/roles/batch`, {
          data: selectedRoles,
        });
        toast.success(`Successfully deleted ${selectedRoles.length} role(s)`);
        setSelectedRoles([]);
        setSelectAll(false);
        fetchRoles();
        markPageAsCompleted("User Role");
      } catch (error) {
        console.error("Error deleting roles:", error);
      } finally {
        setShowWarningPopup(false);
      }
    });
    setShowWarningPopup(true);
  };

  const handleBulkUpdateClick = () => {
    if (bulkUpdateEnabled) {
      const sequences = editableRoles.map((role) => role.roleSequence);

      const hasEmptySequence = sequences.some(
        (seq) => seq === "" || seq === null || seq === undefined,
      );

      if (hasEmptySequence) {
        toast.error("All sequences must be filled out before submitting.");
        return;
      }

      const payload = editableRoles.map((role) => ({
        roleId: role.roleId,
        roleName: role.roleName,
        roleSequence: role.roleSequence,
        roleStatus: role.roleStatus.toUpperCase(),
        companyId: parseInt(process.env.REACT_APP_COMPANY_ID) || role.companyId,
      }));

      axiosInstance
        .put(`${process.env.REACT_APP_API_BASE_URL}/roles/batch`, payload)
        .then(() => {
          toast.success("Bulk update submitted successfully");
          setUserRoles([...editableRoles]);
          markPageAsCompleted("User Role");
        })
        .catch((error) => {
          console.error("Error updating roles:", error);
        });
    }

    setBulkUpdateEnabled((prev) => !prev);
  };

  const handleInlineEdit = (roleId, field, value) => {
    setEditableRoles((prev) =>
      prev.map((role) =>
        role.roleId === roleId
          ? {
              ...role,
              [field]:
                field === "roleSequence"
                  ? value === ""
                    ? ""
                    : parseInt(value)
                  : value,
            }
          : role,
      ),
    );
  };

  const handleEditRole = (roleId) => {
    const role = userRoles.find((r) => r.roleId === roleId);
    if (role) {
      setIsEditing(true);
      setInitialValues(role);
      setShowForm(true);
    }
  };

  const handleDelete = async (roleId) => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/roles/${roleId}`,
      );
      toast.success("Record deleted successfully.");
      fetchRoles();
      markPageAsCompleted("User Role");
    } catch (error) {
      console.error("Error deleting role:", error);
    }
  };

  const toggleRoleStatus = async (roleId, currentStatus) => {
    const newStatus = currentStatus === "Enabled" ? "Disabled" : "Enabled";
    const endpoint = `${process.env.REACT_APP_API_BASE_URL}/roles/${roleId}/${
      newStatus === "Enabled" ? "enable" : "disable"
    }`;

    try {
      await axiosInstance.put(endpoint);
      setUserRoles((prevRoles) =>
        prevRoles.map((role) =>
          role.roleId === roleId ? { ...role, roleStatus: newStatus } : role,
        ),
      );
      markPageAsCompleted("User Role");
    } catch (error) {
      console.error(`Error changing status to ${newStatus}:`, error);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const existingRoleSequence = userRoles.find(
      (role) =>
        role.roleSequence === values.roleSequence &&
        role.roleId !== values.roleId,
    );

    if (existingRoleSequence) {
      toast.error("Role sequence already exists!");
      setSubmitting(false);
      return;
    }

    try {
      const updatedValues = {
        ...values,
        roleStatus: values.roleStatus.toUpperCase(),
      };

      if (isEditing) {
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/roles/${updatedValues.roleId}`,
          updatedValues,
        );
        toast.success("Role updated successfully.");
      } else {
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/roles`,
          updatedValues,
        );
        toast.success("Role created successfully.");
      }

      fetchRoles();
      resetForm();
      setIsEditing(false);
      setShowForm(false);
      markPageAsCompleted("User Role");
    } catch (error) {
      console.error("Error submitting role:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const transformedData = (bulkUpdateEnabled ? editableRoles : userRoles).map(
    (role) => ({
      roleName: (
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <ColoredCheckbox
            name={`role-${role.roleId}`}
            color="green"
            checked={selectedRoles.includes(role.roleId)}
            onChange={() => handleSelectRole(role.roleId)}
            value={role.roleId}
          />
          {bulkUpdateEnabled ? (
            <input
              type="text"
              value={role.roleName}
              onChange={(e) =>
                handleInlineEdit(role.roleId, "roleName", e.target.value)
              }
              className="inline-edit-input"
              style={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "4px 8px",
                fontSize: "14px",
                minWidth: "120px",
              }}
            />
          ) : (
            <span>{role.roleName}</span>
          )}
        </div>
      ),
      roleSequence: bulkUpdateEnabled ? (
        <input
          type="number"
          value={role.roleSequence}
          onChange={(e) =>
            handleInlineEdit(role.roleId, "roleSequence", e.target.value)
          }
          className="inline-edit-input"
          style={{
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "4px 8px",
            fontSize: "14px",
            width: "80px",
          }}
          min="1"
        />
      ) : (
        role.roleSequence
      ),
      status: (
        <ToggleButton
          isOn={role.roleStatus === "Enabled"}
          onToggle={() => toggleRoleStatus(role.roleId, role.roleStatus)}
          status={role.roleStatus === "Disabled"}
        />
      ),
      actions: (
        <div style={{ display: "flex", gap: "8px" }}>
          <IconButton
            icon="iconamoon:edit-light"
            onClick={() => handleEditRole(role.roleId)}
            variant="edit"
            title="Edit Role"
          />
          <IconButton
            icon="fluent:delete-28-regular"
            onClick={() => handleDelete(role.roleId)}
            variant="delete"
            title="Delete Role"
          />
        </div>
      ),
      originalRole: role,
      searchableText: `${role.roleName} ${role.roleSequence} ${role.roleStatus}`,
    }),
  );

  return (
    <div className="user-role">
      <h2 className="display6">User Role</h2>
      <div className="user-role__actions">
        <button
          className="create-role-button"
          onClick={() => {
            if (showForm) {
              setShowForm(false);
            } else {
              setInitialValues({
                roleId: null,
                roleName: "",
                roleSequence: "",
                roleStatus: "Enabled",
                companyId: parseInt(process.env.REACT_APP_COMPANY_ID),
              });
              setShowForm(true);
              setIsEditing(false);
            }
          }}
          style={showForm ? { backgroundColor: "#ff6692", color: "#fff" } : {}}
        >
          {!showForm && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
          )}
          {showForm ? "Close Role Form" : "Create Role"}
        </button>

        <button
          className="create-role-button"
          onClick={handleBulkUpdateClick}
          style={
            bulkUpdateEnabled
              ? { backgroundColor: "#2b9b39", color: "#fff" }
              : {}
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="icon"
            width="16"
            height="16"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 6h16M4 12h16m-7 6h7"
            />
          </svg>

          {bulkUpdateEnabled ? (
            <span>
              <span className="submit-text">Submit</span>{" "}
              <span className="bulk-update-text">Bulk Update</span>
            </span>
          ) : (
            <span>
              <span className="enable-text">Enable</span>{" "}
              <span className="bulk-update-text">Bulk Update</span>
            </span>
          )}
        </button>

        <ButtonWithIcon
          icon="fluent:delete-28-regular"
          text={`Bulk Delete (${selectedRoles.length})`}
          onClick={handleBatchDelete}
          className={`cancel-button ${
            selectedRoles.length === 0 ? "disabled-btn" : ""
          }`}
          disabled={selectedRoles.length === 0}
        />
      </div>
      {showForm && (
        <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={userRoleValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-2">
                  {isEditing ? "Edit Role" : "Create New Role"}
                </h3>

                <div className="user-role-form__grid">
                  <div className="userrole-form-group">
                    <label className="label">Role Name</label>
                    <Field
                      type="text"
                      name="roleName"
                      className="form-input"
                      placeholder="Eg: Manager"
                    />
                    <div className="error-placeholder">
                      <ErrorMessage
                        name="roleName"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>

                  <div className="userrole-form-group">
                    <label className="label">Role Sequence</label>
                    <Field
                      type="number"
                      name="roleSequence"
                      className="form-input"
                      placeholder="Role hierarchy number (1 highest)"
                      min="1"
                    />
                    <div className="error-placeholder">
                      <ErrorMessage
                        name="roleSequence"
                        component="div"
                        className="error-message"
                      />
                    </div>

                    <div className="helper-placeholder">
                      <RoleSequenceHelper userRoles={userRoles} />
                    </div>
                  </div>

                  <div className="userrole-form-group button-group">
                    <label className="label invisible">Submit</label>
                    <ButtonWithIcon
                      type="submit"
                      icon="tdesign:send"
                      text={isEditing ? "Update Role" : "Create Role"}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      <GeneralTable
        title="User Roles"
        columns={tableColumns}
        data={transformedData}
        selectedCount={selectedRoles.length}
        showPagination={true}
      />

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={() => popupAction()}
        onDecline={() => setShowWarningPopup(false)}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${selectedRoles.length} selected role(s)?`}
        acceptText="Yes, Delete"
        declineText="Cancel"
      />
    </div>
  );
};

export default UserRole;
